import { Box, Checkbox, FormControlLabel } from "@mui/material";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import {
    addDestination,
    changeDestination,
    getDestination,
    searchDestinationDisp,
} from "../../api/destinations";
import { getPortCommunities } from "../../api/portCommunity";
import TimeZonesInput from "../../components/TimeZonesInput";
import { getFieldRequiredMessage, showBackendMessage } from "../../helpers/messagesHelper";
import Messages from "../../localization/Messages";
import {
    addEditDestinationInit,
    currencies,
    Currency,
    IAddEditDestination,
} from "../../models/destination";
import { IPortCommunity } from "../../models/portCommunity";
import { searchParamsInit } from "../../models/searchParams";
import Progress from "../Progress/Progress";
import { EditHolidaysButton } from "./components/EditHolidaysButton/EditHolidaysButton";
import PortDocumentsEditor from "./components/PortDocumentsEditor/PortDocumentsEditor";
import { useFetchExpenseTypes } from "../../hooks/useFetchExpenseTypes";
import { IExpenseType } from "../../models/expenseType";
import { mergeDestinationZonesFromRequest } from "../../actions/destinationsAction";

type DestinationErrors = {
    [Key in keyof Partial<IAddEditDestination>]: string;
};
const validationRegex = {
    tel: /^\+?[0-9\-/ ]*$/,
};

const parseAccountingEmails = (items: string[]): string[] => {
    const emails = items
        .flatMap(item => item.split(/[,;\s]+/))
        .map(item => item.trim())
        .filter(Boolean);
    return Array.from(new Set(emails));
};

export interface IDestinationForm {
    setIsExistingUser?: React.Dispatch<React.SetStateAction<boolean | null>>;
}

/**
 * Renders the Destination form displayed on the Manage Destination page.
 * This component allows users to add or edit destination information.
 */
export const DestinationForm = ({ setIsExistingUser }: IDestinationForm) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const [destination, setDestination] = useState(addEditDestinationInit);
    const [accountingInput, setAccountingInput] = useState("");
    const [errors, setErrors] = useState<DestinationErrors>({});
    const [loading, setLoading] = useState(false);
    const [portCommunities, setPortCommunities] = useState<IPortCommunity[]>([]);
    const { expenseTypes } = useFetchExpenseTypes({
        limit: 1000,
        filter: { $and: [{ custom: false }] },
    });

    const handleAddAccountingEmails = (incoming: string[]) => {
        const currentList = destination.accountingEmail
            ? destination.accountingEmail
                  .split(",")
                  .map(e => e.trim())
                  .filter(Boolean)
            : [];
        const updated = parseAccountingEmails([...currentList, ...incoming]);
        onChange({ accountingEmail: updated.join(",") });
    };

    const commitAccountingInput = () => {
        if (accountingInput.trim()) {
            handleAddAccountingEmails([accountingInput]);
            setAccountingInput("");
        }
    };

    const fetchDestination = useCallback(async () => {
        setLoading(true);
        try {
            const destinations = await searchDestinationDisp(searchParamsInit);
            // only one destination is returned for user, zero for new user
            if (destinations.data.rows.length > 0) {
                // existing user
                const res = await getDestination(destinations.data.rows[0].id);
                setDestination(res.data);
                dispatch(mergeDestinationZonesFromRequest([res.data]));
            } else {
                // new user
                setDestination(addEditDestinationInit);
            }
        } catch {
            dispatch(showBackendMessage(intl, "error", "fetching", Messages.destination));
        } finally {
            setLoading(false);
        }
    }, [dispatch, intl]);

    useEffect(() => {
        fetchDestination();
    }, [fetchDestination]);

    const fetchPortCommunities = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getPortCommunities();
            setPortCommunities(res.data ?? []);
        } catch {
            dispatch(showBackendMessage(intl, "error", "fetching", Messages.portCommunities));
        } finally {
            setLoading(false);
        }
    }, [dispatch, intl]);

    useEffect(() => {
        fetchPortCommunities();
    }, [fetchPortCommunities]);

    const validateDestination = (dest: IAddEditDestination = destination) => {
        // TODO: add validation regex
        const newErrors: DestinationErrors = {};
        if (!dest.name) newErrors.name = getFieldRequiredMessage(intl, Messages.name);
        if (!dest.address) newErrors.address = getFieldRequiredMessage(intl, Messages.address);
        if (!dest.zip) newErrors.zip = getFieldRequiredMessage(intl, Messages.zip);
        if (!dest.city) newErrors.city = getFieldRequiredMessage(intl, Messages.city);
        if (!dest.community)
            newErrors.community = getFieldRequiredMessage(intl, Messages.community);
        if (!dest.tel)
            newErrors.tel = getFieldRequiredMessage(intl, Messages.contactTelAdministration);
        if (!dest.email) newErrors.email = getFieldRequiredMessage(intl, Messages.contactMail);
        if (!dest.accountingEmail)
            newErrors.accountingEmail = getFieldRequiredMessage(intl, Messages.accountingEmail);
        if (!dest.currency) newErrors.currency = intl.formatMessage(Messages.currencyIsRequired);
        if (!dest.zone) newErrors.zone = intl.formatMessage(Messages.timeZoneIsRequired);
        if ((dest.invoiceNoPrefix?.length ?? 0) > 12)
            newErrors.invoiceNoPrefix = intl.formatMessage(Messages.invoiceNoPrefixTooLong);
        if (
            typeof dest.invoiceNoLength === "number" &&
            (dest.invoiceNoLength < 4 || dest.invoiceNoLength > 9)
        )
            newErrors.invoiceNoLength = intl.formatMessage(Messages.invoiceNoLengthInvalid);
        return newErrors;
    };

    const handleSubmit = async () => {
        let updatedDestination = destination;
        if (accountingInput.trim()) {
            const currentList = destination.accountingEmail
                ? destination.accountingEmail
                      .split(",")
                      .map(e => e.trim())
                      .filter(Boolean)
                : [];
            const updated = parseAccountingEmails([...currentList, accountingInput]);
            updatedDestination = { ...destination, accountingEmail: updated.join(",") };
            setDestination(updatedDestination);
            setAccountingInput("");
        }
        const newErrors = validateDestination(updatedDestination);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
        } else {
            try {
                if (!updatedDestination.id) {
                    const response = await addDestination(updatedDestination);
                    setDestination(response.data);
                    dispatch(mergeDestinationZonesFromRequest([response.data]));
                    dispatch(showBackendMessage(intl, "success", "creating", Messages.destination));
                    setIsExistingUser?.(true);
                } else {
                    const response = await changeDestination(updatedDestination);
                    setDestination(response.data);
                    dispatch(mergeDestinationZonesFromRequest([response.data]));
                    dispatch(showBackendMessage(intl, "success", "updating", Messages.destination));
                }
            } catch {
                dispatch(showBackendMessage(intl, "error", "updating", Messages.destination));
            }
        }
    };

    const onChange = (diff: Partial<IAddEditDestination>) => {
        setDestination(prev => ({ ...prev, ...diff }));
        const clearErros = Object.fromEntries(Object.entries(diff).map(([k]) => [k, undefined]));
        setErrors({ ...errors, ...clearErros });
    };

    const CopyLinkToClipboardButton = ({
        buttonText,
        endpoint,
    }: {
        buttonText: string;
        endpoint: string;
    }) => {
        const [buttonTextState, setButtonTextState] = useState(buttonText);
        const linkCopyText = window.location.origin + endpoint;

        const copyToClipboard = () => {
            navigator.clipboard
                .writeText(linkCopyText)
                .then(() => {
                    setButtonTextState(intl.formatMessage(Messages.copyAllocationPlanLinkSuccess));
                    setTimeout(() => setButtonTextState(buttonText), 2000);
                })
                .catch(err => console.error("Failed to copy: ", err));
        };

        return (
            <Button variant="contained" onClick={copyToClipboard}>
                {buttonTextState}
            </Button>
        );
    };

    return (
        <>
            {loading ? (
                <Progress />
            ) : (
                <div className="destination-form-container">
                    {!destination.id && (
                        <Alert severity="warning" className="destination-alert">
                            You have to enter destination data to start using the Dispatcher FGKS
                            module.
                        </Alert>
                    )}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <h2>{intl.formatMessage(Messages.editDestination)}</h2>
                        <Box display="flex" gap={1.5} paddingRight={2}>
                            {destination.holidays !== undefined && (
                                <EditHolidaysButton
                                    destination={destination}
                                    holidays={destination.holidays}
                                    onSaveSuccess={holidays => onChange({ holidays })}
                                />
                            )}
                            <CopyLinkToClipboardButton
                                endpoint={`/plan/public/${destination?.id}`}
                                buttonText={intl.formatMessage(Messages.copyAllocationPlanLink)}
                            />
                            <CopyLinkToClipboardButton
                                endpoint={`/plan/public/${destination?.id}?plus=true`}
                                buttonText={intl.formatMessage(Messages.copyAllocationPlanPlusLink)}
                            />
                            <Button variant="contained" onClick={handleSubmit}>
                                {intl.formatMessage(Messages.saveChanges)}
                            </Button>
                        </Box>
                    </Box>
                    <form className="column-form three-column-form">
                        <TextField
                            required
                            error={Boolean(errors.name)}
                            value={destination.name}
                            label={intl.formatMessage(Messages.name)}
                            onChange={e => onChange({ name: e.target.value })}
                            helperText={errors.name}
                            variant="filled"
                        />
                        <TextField
                            required
                            error={Boolean(errors.address)}
                            value={destination.address}
                            label={intl.formatMessage(Messages.address)}
                            onChange={e => onChange({ address: e.target.value })}
                            helperText={errors.address}
                            variant="filled"
                        />
                        <TextField
                            required
                            error={Boolean(errors.zip)}
                            value={destination.zip}
                            label={intl.formatMessage(Messages.zip)}
                            onChange={e => onChange({ zip: e.target.value })}
                            helperText={errors.zip}
                            variant="filled"
                        />
                        <TextField
                            required
                            error={Boolean(errors.city)}
                            value={destination.city}
                            label={intl.formatMessage(Messages.city)}
                            onChange={e => onChange({ city: e.target.value })}
                            helperText={errors.city}
                            variant="filled"
                        />
                        <TimeZonesInput
                            onChange={zone => onChange({ zone })}
                            value={destination.zone}
                            error={errors.zone}
                        />
                        <Autocomplete
                            options={currencies}
                            disableClearable
                            renderInput={params => (
                                <TextField
                                    variant="filled"
                                    {...params}
                                    label={intl.formatMessage(Messages.currency)}
                                    error={Boolean(errors.currency)}
                                    helperText={errors.currency}
                                />
                            )}
                            value={currencies.find(c => destination.currency === c)}
                            onChange={(_, value) => onChange({ currency: value ?? Currency.EUR })}
                        />
                        <Autocomplete
                            options={portCommunities}
                            getOptionLabel={o => o.name}
                            renderInput={params => (
                                <TextField
                                    variant="filled"
                                    {...params}
                                    required
                                    error={Boolean(errors.community)}
                                    value={destination.community}
                                    label={intl.formatMessage(Messages.community)}
                                    onChange={e => onChange({ community: e.target.value })}
                                    helperText={errors.community}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.name}
                                </li>
                            )}
                            onChange={(_, value: IPortCommunity | null) =>
                                onChange({ community: value?.name })
                            }
                            value={
                                portCommunities.find(
                                    community => community.name === destination.community,
                                ) ?? null
                            }
                        />
                        <TextField
                            required
                            error={Boolean(errors.tel)}
                            value={destination.tel}
                            label={intl.formatMessage(Messages.contactTelAdministration)}
                            onChange={e => {
                                validationRegex.tel.test(e.target.value) &&
                                    onChange({ tel: e.target.value });
                            }}
                            helperText={errors.tel}
                            variant="filled"
                        />
                        <TextField
                            required
                            error={Boolean(errors.email)}
                            value={destination.email}
                            label={intl.formatMessage(Messages.contactMail)}
                            onChange={e => onChange({ email: e.target.value })}
                            helperText={errors.email}
                            variant="filled"
                        />
                        <Autocomplete
                            freeSolo
                            multiple
                            options={[]}
                            inputValue={accountingInput}
                            onInputChange={(_, newInputValue) => setAccountingInput(newInputValue)}
                            value={
                                destination.accountingEmail
                                    ? destination.accountingEmail
                                          .split(",")
                                          .map(e => e.trim())
                                          .filter(Boolean)
                                    : []
                            }
                            onChange={(_, values) => {
                                const parsed = parseAccountingEmails(values as string[]);
                                onChange({ accountingEmail: parsed.join(",") });
                                setAccountingInput("");
                            }}
                            renderInput={params => (
                                <TextField
                                    required
                                    {...params}
                                    variant="filled"
                                    label={intl.formatMessage(Messages.accountingEmail)}
                                    placeholder={
                                        !destination.accountingEmail ? "name@example.com" : ""
                                    }
                                    helperText={
                                        errors.accountingEmail ||
                                        intl.formatMessage(Messages.accountingEmailHelper)
                                    }
                                    error={Boolean(errors.accountingEmail)}
                                    onBlur={commitAccountingInput}
                                    onKeyDown={e => {
                                        if (e.key === "," || e.key === ";") {
                                            e.preventDefault();
                                            commitAccountingInput();
                                        } else if (e.key === "Tab" && accountingInput.trim()) {
                                            commitAccountingInput();
                                        }
                                    }}
                                    onPaste={e => {
                                        const pastedText = e.clipboardData.getData("text");
                                        if (/[,;\s]/.test(pastedText)) {
                                            e.preventDefault();
                                            handleAddAccountingEmails([pastedText]);
                                            setAccountingInput("");
                                        }
                                    }}
                                />
                            )}
                        />
                        <TextField
                            error={Boolean(errors.contactOnSite)}
                            value={destination.contactOnSite}
                            label={intl.formatMessage(Messages.contactOnSite)}
                            onChange={e => onChange({ contactOnSite: e.target.value })}
                            helperText={errors.contactOnSite}
                            variant="filled"
                        />
                        <TextField
                            required
                            error={Boolean(errors.invoiceNoPrefix)}
                            value={destination.invoiceNoPrefix}
                            label={intl.formatMessage(Messages.invoiceNoPrefix)}
                            onChange={e => onChange({ invoiceNoPrefix: e.target.value })}
                            helperText={errors.invoiceNoPrefix}
                            variant="filled"
                        />
                        <TextField
                            required
                            error={Boolean(errors.invoiceNoLength)}
                            value={destination.invoiceNoLength}
                            label={intl.formatMessage(Messages.invoiceNoLength)}
                            onChange={e => onChange({ invoiceNoLength: Number(e.target.value) })}
                            helperText={errors.invoiceNoLength}
                            variant="filled"
                        />
                        <FormControlLabel
                            label={intl.formatMessage(Messages.active)}
                            control={
                                <Checkbox
                                    checked={Boolean(destination.active)}
                                    onChange={e =>
                                        onChange({
                                            active: e.target.checked ? 1 : 0,
                                        })
                                    }
                                />
                            }
                        />
                        <h3
                            style={{
                                gridColumn: "span 3",
                                margin: "16px 0 8px 0",
                                fontSize: "1.17em",
                                fontWeight: 400,
                            }}
                        >
                            {intl.formatMessage(Messages.expenseTypes)}
                        </h3>
                        <Autocomplete
                            options={expenseTypes}
                            getOptionLabel={(option: IExpenseType) => option.name}
                            renderInput={params => (
                                <TextField
                                    variant="filled"
                                    {...params}
                                    label={intl.formatMessage(Messages.expenseTypeArrivalDay)}
                                    error={Boolean(errors.expenseTypeArrivalDay)}
                                    helperText={errors.expenseTypeArrivalDay}
                                />
                            )}
                            value={
                                expenseTypes.find(
                                    et => et.id === destination.expenseTypeArrivalDay,
                                ) || null
                            }
                            onChange={(_, value: IExpenseType | null) =>
                                onChange({ expenseTypeArrivalDay: value?.id || "" })
                            }
                        />
                        <Autocomplete
                            options={expenseTypes}
                            getOptionLabel={(option: IExpenseType) => option.name}
                            renderInput={params => (
                                <TextField
                                    variant="filled"
                                    {...params}
                                    label={intl.formatMessage(Messages.expenseTypeLayDays)}
                                    error={Boolean(errors.expenseTypeLayDays)}
                                    helperText={errors.expenseTypeLayDays}
                                />
                            )}
                            value={
                                expenseTypes.find(et => et.id === destination.expenseTypeLayDays) ||
                                null
                            }
                            onChange={(_, value: IExpenseType | null) =>
                                onChange({ expenseTypeLayDays: value?.id || "" })
                            }
                        />
                        <Autocomplete
                            options={expenseTypes}
                            getOptionLabel={(option: IExpenseType) => option.name}
                            renderInput={params => (
                                <TextField
                                    variant="filled"
                                    {...params}
                                    label={intl.formatMessage(Messages.expenseTypeWasteDisposal)}
                                    error={Boolean(errors.expenseTypeWasteDisposal)}
                                    helperText={errors.expenseTypeWasteDisposal}
                                />
                            )}
                            value={
                                expenseTypes.find(
                                    et => et.id === destination.expenseTypeWasteDisposal,
                                ) || null
                            }
                            onChange={(_, value: IExpenseType | null) =>
                                onChange({ expenseTypeWasteDisposal: value?.id || "" })
                            }
                        />
                        <Autocomplete
                            options={expenseTypes}
                            getOptionLabel={(option: IExpenseType) => option.name}
                            renderInput={params => (
                                <TextField
                                    variant="filled"
                                    {...params}
                                    label={intl.formatMessage(Messages.expenseTypeWayFee)}
                                    error={Boolean(errors.expenseTypeWayFee)}
                                    helperText={errors.expenseTypeWayFee}
                                />
                            )}
                            value={
                                expenseTypes.find(et => et.id === destination.expenseTypeWayFee) ||
                                null
                            }
                            onChange={(_, value: IExpenseType | null) =>
                                onChange({ expenseTypeWayFee: value?.id || "" })
                            }
                        />
                        <Autocomplete
                            options={expenseTypes}
                            getOptionLabel={(option: IExpenseType) => option.name}
                            renderInput={params => (
                                <TextField
                                    variant="filled"
                                    {...params}
                                    label={intl.formatMessage(Messages.expenseTypeWorkRegular)}
                                    error={Boolean(errors.expenseTypeWorkRegular)}
                                    helperText={errors.expenseTypeWorkRegular}
                                />
                            )}
                            value={
                                expenseTypes.find(
                                    et => et.id === destination.expenseTypeWorkRegular,
                                ) || null
                            }
                            onChange={(_, value: IExpenseType | null) =>
                                onChange({ expenseTypeWorkRegular: value?.id || "" })
                            }
                        />
                        <Autocomplete
                            options={expenseTypes}
                            getOptionLabel={(option: IExpenseType) => option.name}
                            renderInput={params => (
                                <TextField
                                    variant="filled"
                                    {...params}
                                    label={intl.formatMessage(Messages.expenseTypeWorkSaturday)}
                                    error={Boolean(errors.expenseTypeWorkSaturday)}
                                    helperText={errors.expenseTypeWorkSaturday}
                                />
                            )}
                            value={
                                expenseTypes.find(
                                    et => et.id === destination.expenseTypeWorkSaturday,
                                ) || null
                            }
                            onChange={(_, value: IExpenseType | null) =>
                                onChange({ expenseTypeWorkSaturday: value?.id || "" })
                            }
                        />
                        <Autocomplete
                            options={expenseTypes}
                            getOptionLabel={(option: IExpenseType) => option.name}
                            renderInput={params => (
                                <TextField
                                    variant="filled"
                                    {...params}
                                    label={intl.formatMessage(Messages.expenseTypeWorkSunday)}
                                    error={Boolean(errors.expenseTypeWorkSunday)}
                                    helperText={errors.expenseTypeWorkSunday}
                                />
                            )}
                            value={
                                expenseTypes.find(
                                    et => et.id === destination.expenseTypeWorkSunday,
                                ) || null
                            }
                            onChange={(_, value: IExpenseType | null) =>
                                onChange({ expenseTypeWorkSunday: value?.id || "" })
                            }
                        />
                    </form>
                    {destination.id && <PortDocumentsEditor destinationId={destination.id} />}
                </div>
            )}
        </>
    );
};

export default DestinationForm;
